import { getFantasticFansPaginated } from '@services/youtube-platform.service';

import type {
  FantasticFansCursor,
  FantasticFansFilter,
  FantasticFansPage,
  FantasticFansResponse,
} from './types';

export const PER_PAGE = 20;

/** `getWithAuth` sets no axios timeout (only `postWithAuth` does), so a hung GET never settles. */
const REQUEST_DEADLINE_MS = 30_000;

/**
 * `@services/youtube-platform.service` is untyped JS. Assert the one function we
 * need at this boundary rather than converting the whole module.
 */
const fetchPage = getFantasticFansPaginated as (
  streamId: string,
  params: {
    filter: FantasticFansFilter;
    perPage: number;
    afterCount?: number;
    afterName?: string;
  },
) => Promise<FantasticFansResponse>;

export class FantasticFansError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FantasticFansError';
  }
}

const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(
    () => reject(new FantasticFansError('Fantastic Fans request timed out')),
    ms,
  );
  promise.then(resolve, reject).finally(() => clearTimeout(timer));
});

/**
 * Fetches one page of fans. The API resolves with `{ status: false }` rather than
 * rejecting on failure, so translate that into a throw for react-query.
 */
export const fetchFansPage = async (
  streamId: string,
  filter: FantasticFansFilter,
  cursor: FantasticFansCursor | null,
): Promise<FantasticFansPage> => {
  const response = await withDeadline(
    fetchPage(streamId, {
      filter,
      perPage: PER_PAGE,
      ...(cursor ? { afterCount: cursor.afterCount, afterName: cursor.afterName } : {}),
    }),
    REQUEST_DEADLINE_MS,
  );

  if (!response?.status) {
    throw new FantasticFansError('Failed to fetch the fantastic fans in the comments');
  }

  return response.entity?.rows ?? [];
};
